/**
 * The API client.
 *
 * Thin on purpose. Credentials ride on the session cookie, so there is no
 * token to store in JavaScript — which is the point: an XSS that can read
 * `localStorage` gets nothing here, because the cookie is HttpOnly and
 * unreachable from script.
 */

export interface ApiError {
  code: string;
  message: string;
  field?: string;
  requestId?: string;
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = "ApiFailure";
  }

  /** True when the session is gone and the shell should show the sign-in screen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get needsTotp(): boolean {
    return this.error.code === "totp_required";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    // Same-origin: the cookie is SameSite=Strict, so it is only ever sent here.
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body as { error?: ApiError } | null)?.error ?? {
      code: "request_failed",
      message: `Request failed with ${response.status}.`,
    };
    throw new ApiFailure(response.status, error);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ---------------------------------------------------------------- types

export interface Me {
  kind: "user" | "api_key";
  user?: PublicUser;
  mustChangePassword?: boolean;
}

export interface PublicUser {
  id: string;
  email: string;
  role: "owner" | "admin" | "operator" | "viewer";
  displayName: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Proposal {
  id: string;
  model: string;
  text: string;
  authorId: string;
  authorName: string;
  status: "pending" | "approved" | "rejected";
  channelId: string | null;
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  reason: string | null;
}

export interface StatsOverview {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
  last24h: number;
  approvalRate: number | null;
  activeApiKeys: number;
  activeWebhooks: number;
  unreadConversations: number;
  oldestPendingAgeSeconds: number | null;
}

export interface BotStatus {
  connected: boolean;
  readyAt: string | null;
  pingMs: number | null;
  guildCount: number;
  username: string | null;
  lastError: string | null;
  reconnectCount: number;
}

export interface ThroughputPoint {
  bucket: string;
  submitted: number;
  approved: number;
  rejected: number;
}

export interface ApiKey {
  id: string;
  name: string;
  environment: "live" | "test";
  displayPrefix: string;
  last4: string;
  scopes: string[];
  status: "active" | "expired" | "revoked";
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  requestCount: number;
}

export interface Conversation {
  id: string;
  kind: "dm" | "channel";
  title: string;
  counterpartName: string | null;
  messageCount: number;
  unreadCount: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  direction: "inbound" | "outbound";
  authorKind: "user" | "bot" | "system";
  authorName: string;
  content: string;
  createdAt: string;
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string;
  events: string[];
  isActive: boolean;
  consecutiveFailures: number;
  disabledReason: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  createdAt: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * Subscribe to the live event stream.
 *
 * `EventSource` reconnects on its own with a growing backoff, which is most of
 * why SSE was chosen over a WebSocket: there is no reconnect logic to write
 * and no keepalive to get wrong.
 */
export function subscribe(
  events: string[],
  onEvent: (event: { id: string; type: string; createdAt: string; data: unknown }) => void,
  onStateChange?: (connected: boolean) => void,
): () => void {
  const query = events.length > 0 ? `?events=${encodeURIComponent(events.join(","))}` : "";
  const source = new EventSource(`/api/v1/stream${query}`, { withCredentials: true });

  source.addEventListener("open", () => onStateChange?.(true));
  source.addEventListener("error", () => onStateChange?.(false));
  source.addEventListener("message", (e) => {
    try {
      onEvent(JSON.parse((e as MessageEvent<string>).data));
    } catch {
      // A malformed frame is not worth tearing the stream down for.
    }
  });

  return () => source.close();
}
